import AsyncStorage from "@react-native-async-storage/async-storage";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";
import {
  GUEST_PROGRESS_QUERY_KEY,
  GUEST_STARTER_QUERY_KEY,
  GUEST_TIMERS_QUERY_KEY,
} from "~/lib/guest-data";

const GUEST_QUERY_KEY_PREFIXES = new Set<unknown>([
  GUEST_STARTER_QUERY_KEY[0],
  GUEST_PROGRESS_QUERY_KEY[0],
  GUEST_TIMERS_QUERY_KEY[0],
]);

const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
  key: "doughbro:query-cache",
  // The default 1s throttle is tuned for chatty server-state caches; a
  // guest's starter/timers/progress are the only things this app persists,
  // written one deliberate edit at a time, so a short throttle trades a few
  // extra AsyncStorage writes for not losing an edit to a reload seconds
  // later.
  throttleTime: 100,
});

/**
 * Only guest data is persisted. Authenticated data is always refetched from
 * the API (the app is online-only for signed-in users), so caching it to disk
 * would just risk showing stale data after a sign-out / sign-in as someone
 * else.
 */
export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister: asyncStoragePersister,
  // The guest data has no server copy — this persisted entry IS the data,
  // not a cache of it — so it must never be aged out.
  maxAge: Infinity,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      GUEST_QUERY_KEY_PREFIXES.has(query.queryKey[0]),
  },
};
